import {
  EFreightForShipperStatus,
  EFreightTransportGoodsType,
  EStopStatus,
  IFreightBasicForShipper,
  IFreightDetailedForShipper,
  IFreightVehicle,
  IInvoiceReferenceBasic,
  IStopBasicForShipper,
  IStopDetailedForShipper,
} from '@okcargo-apps/shared-domain';
import { ICompany } from '@okcargo-apps/shared-domain-ionic';
import {
  mapFreightBillingStatusEnum,
  mapLoadingTypeEnum,
  mapStopStatusEnum,
  mapStopTypeEnum,
  mapTransportGoodsTypeEnum,
  mapVehicleTypeEnum,
} from '../freight-carrier/freight-carrier-enum-mapper';
import { transformToEFreightForShipperStatus } from './freight-shipper-enum-mapper';
import {
  CreateFreightWithStopsAndAssignationsCommand,
  FreightDocumentType,
  FreightLoadingType,
  FreightSource,
  FreightStopPayload,
  FreightStopType,
  FreightTransportGoodsType,
  FreightTransportInfoPayload,
  FreightType,
  TemperatureUnit,
  UpdateFreightCommand,
  UpdateFreightStopCommand,
  VehicleType,
} from '@okcargo/command-processor';
import {
  FreightBasicForShipperResponse,
  FreightDetailForShipperResponse,
  FreightStopStatus,
} from '@okcargo/query-processor';

export function transformToIFreightBasicForShipper(
  freightBasicResponse: FreightBasicForShipperResponse,
  freight: IFreightBasicForShipper,
): IFreightBasicForShipper {
  let isAnyStopStartedOrFinished = false;

  freight = {
    ...freight,
    id: freightBasicResponse.id,
    stopsBasic: [],
    numberOfStops: freightBasicResponse.stops.length,
    reference: freightBasicResponse.reference,
    // TODO If the user does not have capabilities, do not add drivers or vehicles ???
    drivers: [],
    vehicles: [],
    price: freightBasicResponse.price ?? undefined,
    // TODO assign freightDetailedResponse.offerAcceptedAt ?? undefined,
    offerAcceptedAt: undefined,
    scheduledKm: freightBasicResponse.distance ?? undefined,
    deliveryNoteInPaperRequired: freightBasicResponse.deliveryNoteInPaperRequired ?? undefined,
    // billingStatus: freightBasicResponse.billingStatus
    //   ? mapFreightBillingStatusEnum(freightBasicResponse.billingStatus)
    //   : undefined,
    // invoiceList: [],
  };

  const orderedStops = [...freightBasicResponse.stops].sort((a, b) => a.position - b.position);

  orderedStops.forEach((stopResponse) => {
    const stop: IStopBasicForShipper = {
      id: stopResponse.id,
      position: stopResponse.position,
      type: mapStopTypeEnum(stopResponse.type),
      status: mapStopStatusEnum(stopResponse.stopStatus),
      since: new Date(stopResponse.since),
      until: new Date(stopResponse.until),
      address: {
        company: stopResponse.companyName,
        street: stopResponse.street,
        // extraLine: stopResponse.extraLine,
        city: stopResponse.city,
        postalCode: stopResponse.postalCode,
        stateCode: stopResponse.stateCode,
        // state: stopResponse.state, // TODO calculate this
        countryCode: stopResponse.countryCode,
        // country: stopResponse.country, // TODO calculate this
      },
      // reference: stopResponse.reference, // not necessary yet
    };

    freight.stopsBasic.push(stop);

    if (
      stopResponse.stopStatus === FreightStopStatus.STARTED ||
      stopResponse.stopStatus === FreightStopStatus.FINISHED
    ) {
      isAnyStopStartedOrFinished = true;
    }
  });

  const nextStop = freight.stopsBasic.find(
    (stop) => stop.status === EStopStatus.STARTED || stop.status === EStopStatus.UNSTARTED,
  );

  const targetStop = nextStop ?? freight.stopsBasic[freight.stopsBasic.length - 1];

  freight.nextStopId = targetStop.id;
  freight.nextStopType = targetStop.type;
  freight.nextStopStatus = targetStop.status;

  // TODO remove casting with `any` when query processor is fixed
  // if (freightBasicResponse.status !== undefined) {
  if ((freightBasicResponse as any).status !== undefined) {
    freight.status = transformToEFreightForShipperStatus(
      // freightBasicResponse.status,
      (freightBasicResponse as any).status,
      isAnyStopStartedOrFinished,
      freight.price,
      freight.offerAcceptedAt,
    );

    // It prevents issues if the freight status is inconsistent with the stops status:
    // - If last stop finished but freight not finished, mark freight as finished
    if (
      freight.stopsBasic.length >= 2 &&
      freight.status !== EFreightForShipperStatus.FINISHED &&
      freight.stopsBasic[freight.stopsBasic.length - 1].status === EStopStatus.FINISHED
    ) {
      freight.status = EFreightForShipperStatus.FINISHED;
    }
    // - If last stop is not finished but freight is finished, mark all stops as finished
    if (
      freight.stopsBasic.length >= 2 &&
      freight.status === EFreightForShipperStatus.FINISHED &&
      freight.stopsBasic[freight.stopsBasic.length - 1].status !== EStopStatus.FINISHED
    ) {
      freight.stopsBasic.forEach((stop) => {
        stop.status = EStopStatus.FINISHED;
      });
    }
  } else {
    freight.status = EFreightForShipperStatus.DEFAULT;
  }

  if (freightBasicResponse.driverId && freightBasicResponse.driverName) {
    freight.drivers.push({
      id: freightBasicResponse.driverId,
      name: freightBasicResponse.driverName,
    });
  }

  freightBasicResponse.vehicles.forEach((vehicleResponse) => {
    const vehicle: IFreightVehicle = {
      id: vehicleResponse.id,
      plate: vehicleResponse.plate,
    };
    freight.vehicles.push(vehicle);
  });

  // freightBasicResponse.invoiceList.forEach((invoiceResponse) => {
  //   const invoce: IInvoiceBasic = {
  //     id: invoiceResponse.id,
  //     number: invoiceResponse.number,
  //   };
  //   freight.invoiceList.push(invoce);
  // });

  return freight;
}

export function transformToIFreightDetailForShipper(
  freightDetailedResponse: FreightDetailForShipperResponse,
  freight: IFreightDetailedForShipper,
): IFreightDetailedForShipper {
  let isAnyStopStartedOrFinished = false;

  freight = {
    ...freight,
    id: freightDetailedResponse.id,
    stopsDetailed: [],
    numberOfStops: freightDetailedResponse.freightStopResponses.length,
    reference: freightDetailedResponse.shipperReference,
    okcargoReference: freightDetailedResponse.internalReference,
    // TODO If the user does not have capabilities, do not add drivers or vehicles ???
    drivers: [],
    vehicles: [],
    vehicleTypes: [],
    loadingTypes: [],
    price: freightDetailedResponse.price ?? undefined,
    // TODO assign freightDetailedResponse.offerAcceptedAt ?? undefined,
    offerAcceptedAt: undefined,
    scheduledKm: freightDetailedResponse.distance ?? undefined,
    deliveryNoteInPaperRequired: freightDetailedResponse.deliveryNoteInPaperRequired ?? undefined,
    billingStatus: freightDetailedResponse.billingStatus
      ? mapFreightBillingStatusEnum(freightDetailedResponse.billingStatus)
      : undefined,
    invoiceList: [],
    transportGoodsType: mapTransportGoodsTypeEnum(freightDetailedResponse.transportGoodsType),
    waste: false, // is subsequently modified if transportGoodsType === WASTE
    instructions: freightDetailedResponse.instructions ?? '',
    // TODO review properties:
    shipperSiteId: freightDetailedResponse.siteId,
    shipperSiteName: freightDetailedResponse.siteName,
    shipperContactId: freightDetailedResponse.contactId,
    shipperContactName: freightDetailedResponse.contactName,
  };

  const orderedStops = [...freightDetailedResponse.freightStopResponses].sort((a, b) => a.position - b.position);

  orderedStops.forEach((stopResponse) => {
    const stop: IStopDetailedForShipper = {
      id: stopResponse.id,
      // freightId: stopResponse.freightId,
      position: stopResponse.position,
      type: mapStopTypeEnum(stopResponse.type),
      status: stopResponse.arrivedAt
        ? stopResponse.departedAt
          ? EStopStatus.FINISHED
          : EStopStatus.STARTED
        : EStopStatus.UNSTARTED,
      instructions: stopResponse.instructions,
      since: new Date(stopResponse.since),
      until: new Date(stopResponse.until),
      address: {
        company: stopResponse.companyPayload.name,
        phone: stopResponse.companyPayload.phone ?? undefined,
        taxId: stopResponse.companyPayload.taxId ?? undefined,
        street: stopResponse.addressPayload.street,
        extraLine: stopResponse.addressPayload.extraLine ?? undefined,
        city: stopResponse.addressPayload.city,
        postalCode: stopResponse.addressPayload.postalCode,
        // state: stopResponse.addressPayload.state,
        stateCode: stopResponse.addressPayload.stateCode,
        // country: stopResponse.addressPayload.country,
        countryCode: stopResponse.addressPayload.countryCode,
        coords: stopResponse.addressPayload.pointPayload ?? undefined, // map coords?
        addressbookId: stopResponse.addressPayload.id ?? undefined,
      },

      goodsDescription: stopResponse.goodsDescription ?? undefined,
      reference: stopResponse.reference ?? undefined,
      netWeight: stopResponse.netWeight ?? undefined,
      packages: stopResponse.packages ?? undefined,
      unloadedNetWeight: stopResponse.unloadedNetWeight ?? undefined,
      unloadedPackages: stopResponse.unloadedPackages ?? undefined,
      arrivalDriverAppPoint: stopResponse.arrivalDriverAppPoint ?? undefined,
      arrivalTruckPoint: stopResponse.arrivalTruckPoint ?? undefined,
      reservations: stopResponse.reservations ?? undefined,
      acceptanceDriverAppPoint: stopResponse.acceptanceDriverAppPoint ?? undefined,
      acceptanceTruckPoint: stopResponse.acceptanceTruckPoint ?? undefined,
      acceptanceSignatureImage: stopResponse.acceptanceSignatureImage ?? undefined,
      acceptanceName: stopResponse.acceptanceName ?? undefined,
      acceptanceTaxId: stopResponse.acceptanceTaxId ?? undefined,
      acceptanceAcceptedAt: stopResponse.acceptanceAcceptedAt ?? undefined,
      sealNumber: stopResponse.sealNumber ?? undefined,
      arrivedAt: stopResponse.arrivedAt ? new Date(stopResponse.arrivedAt) : undefined,
      departedAt: stopResponse.departedAt ? new Date(stopResponse.departedAt) : undefined,
    };

    if (stopResponse.arrivedAt !== undefined) {
      isAnyStopStartedOrFinished = true;
    }

    freight.stopsDetailed.push(stop);
  });

  const nextStop = freight.stopsDetailed.find(
    (stop) => stop.status === EStopStatus.STARTED || stop.status === EStopStatus.UNSTARTED,
  );

  const targetStop = nextStop ?? freight.stopsDetailed[freight.stopsDetailed.length - 1];

  freight.nextStopId = targetStop.id;
  freight.nextStopType = targetStop.type;
  freight.nextStopStatus = targetStop.status;

  if (freightDetailedResponse.status !== undefined) {
    freight.status = transformToEFreightForShipperStatus(
      freightDetailedResponse.status,
      isAnyStopStartedOrFinished,
      freight.price,
      freight.offerAcceptedAt,
    );

    // It prevents issues if the freight status is inconsistent with the stops status:
    // - If last stop finished but freight not finished, mark freight as finished
    if (
      freight.stopsDetailed.length >= 2 &&
      freight.status !== EFreightForShipperStatus.FINISHED &&
      freight.stopsDetailed[freight.stopsDetailed.length - 1].status === EStopStatus.FINISHED
    ) {
      freight.status = EFreightForShipperStatus.FINISHED;
    }
    // - If last stop is not finished but freight is finished, mark all stops as finished
    if (
      freight.stopsDetailed.length >= 2 &&
      freight.status === EFreightForShipperStatus.FINISHED &&
      freight.stopsDetailed[freight.stopsDetailed.length - 1].status !== EStopStatus.FINISHED
    ) {
      freight.stopsDetailed.forEach((stop) => {
        stop.status = EStopStatus.FINISHED;
      });
    }
  } else {
    freight.status = EFreightForShipperStatus.DEFAULT;
  }

  if (freightDetailedResponse.driverId && freightDetailedResponse.driverName) {
    freight.drivers.push({
      id: freightDetailedResponse.driverId,
      name: freightDetailedResponse.driverName,
    });
  }

  freightDetailedResponse.vehicles.forEach((vehicleResponse) => {
    const vehicle: IFreightVehicle = {
      id: vehicleResponse.id,
      plate: vehicleResponse.plate,
    };
    freight.vehicles.push(vehicle);
  });

  freightDetailedResponse.invoiceList.forEach((invoiceResponse) => {
    const invoce: IInvoiceReferenceBasic = {
      id: invoiceResponse.id,
      number: invoiceResponse.number,
    };
    freight.invoiceList.push(invoce);
  });

  freightDetailedResponse.vehicleTypes.forEach((vehicleTypeResponse) => {
    const vehicleType = mapVehicleTypeEnum(vehicleTypeResponse);
    freight.vehicleTypes.push(vehicleType);
  });

  freightDetailedResponse.loadingTypes.forEach((loadingTypesResponse) => {
    const loadingType = mapLoadingTypeEnum(loadingTypesResponse);
    freight.loadingTypes.push(loadingType);
  });

  if (freight.transportGoodsType === EFreightTransportGoodsType.WASTE) {
    freight.waste = true;
  }

  // TODO delivery notes, call with new query

  // TODO review this, get a more descriptive title (translate outside, or maybe asign title in component,...)
  freight.title = freight.reference;

  // TODO add the temperatures range if it is a refrigerated load
  // comes from old code (NOT UNCOMMENT)
  // if (
  //   Array.isArray(freightDetailedResponse.transportInfoPayload.transportTemperatureRanges) &&
  //   freightDetailedResponse.transportInfoPayload.transportTemperatureRanges.length > 0
  // ) {
  //   const temperatureRange = freightDetailedResponse.transportInfoPayload.transportTemperatureRanges[0];
  //   const [min, max] = Object.entries(temperatureRange)[0];
  //   freight.temperatureMin = Number(min);
  //   freight.temperatureMax = Number(max);
  //   freight.temperatureUnit = freightDetailedResponse.transportInfoPayload.transportTemperatureUnit || 'CELSIUS';
  // }

  return freight;
}

////
////
////
////
////

// TODO destructuring formData because contains general form info and a IStopDetail[] array
export function transformToCreateFreightWithStopsAndAssignationsCommand(
  freight: IFreightDetailedForShipper,
  account: ICompany,
): CreateFreightWithStopsAndAssignationsCommand {
  const stops = freight.stopsDetailed.map(
    (stop: IStopDetailedForShipper, index: number) =>
      // (stop: any, index: number) =>
      ({
        stopId: stop.id,
        position: index,
        type: stop.type as unknown as FreightStopType,
        // review
        addressPayload: {
          id: stop.address.addressbookId,
          street: stop.address.street,
          extraLine: stop.address.extraLine,
          city: stop.address.city,
          postalCode: stop.address.postalCode,
          // state: stop.address.state,
          stateCode: stop.address.stateCode,
          // country: stop.address.country,
          countryCode: stop.address.countryCode,
          pointPayload: stop.address.coords, // TODO review, comes from address
        },
        // review
        companyPayload: {
          id: null,
          name: stop.address.company,
          taxId: stop.address.taxId,
          phone: stop.address.phone,
          addressPayload: null,
          m2mId: null,
        },
        since: stop.since ? new Date(stop.since).toISOString() : null,
        until: stop.until ? new Date(stop.until).toISOString() : null,
        tare: null, // TODO obtener del formulario?
        grossWeight: null, // TODO obtener del formulario?
        netWeight: stop.netWeight || null,
        goodsDescription: stop.goodsDescription || null,
        packages: stop.packages || null,
        reference: stop.reference || null,
        unloadedNetWeight: stop.unloadedNetWeight || null,
        unloadedPackages: stop.unloadedPackages || null,
        instructions: stop.instructions,
      }) as unknown as FreightStopPayload,
  );

  const payload: CreateFreightWithStopsAndAssignationsCommand = {
    id: crypto.randomUUID(),
    type: FreightType.FULL,
    transportInfoPayload: {
      vehicleTypes: freight.vehicleTypes as unknown as VehicleType,
      loadingTypes: freight.loadingTypes as unknown as FreightLoadingType,
      transportGoodsType: freight.waste ? FreightTransportGoodsType.WASTE : FreightTransportGoodsType.NORMAL,
      // TODO review this
      // transportTemperatureRanges:
      //   freight.temperatureMin && freight.temperatureMax
      //     ? [
      //         {
      //           [freight.temperatureMin.toString()]: freight.temperatureMax.toString(),
      //         },
      //       ]
      //     : [],
      // transportTemperatureUnit: freight.temperatureMin && freight.temperatureMax ? TemperatureUnit.CELSIUS : null,
      transportTemperatureRanges: [],
      transportTemperatureUnit: TemperatureUnit.CELSIUS,
      // exchangePlatform: null,
      // copperInsuranceRequired: null,
      // environmentalIdentificationNumberRequired: null,
    } as unknown as FreightTransportInfoPayload,
    // },
    documentType: FreightDocumentType.ECDP, // revisar si esto puede cambiar o no
    instructions: freight.instructions,
    // carrierObservations: null,
    deliveryNoteInPaper: freight.deliveryNoteInPaperRequired ? true : false,
    stops: stops, // FreightStopPayload[] (see above)
    source: FreightSource.WEB_FORM,
    // shipperId: '', // the backend get it from the user's authentication data
    shipperSiteId: freight.shipperSiteId, // TODO
    shipperContactId: account.id, // the user
    shipperReference: freight.reference,

    // - properties not to include in the command:
    // carrierObservations: '',
    // isDraft: false,
    // salePrice: 0,
    // purchasePrice: 0,
    // isVisibleInMarket: false,
    // userInChargeId: '',
    // userInChargeName: '',
    // shipperBillable: false,
    // carrierBillable: false,
    // estimatedSalePrice: 0,
    // estimatedPurchasePrice: 0,
    // visibleInMarket: false,
    // draft: false,
  } as unknown as CreateFreightWithStopsAndAssignationsCommand;

  return payload;
}

export function transformToUpdateFreightCommand(freight: IFreightDetailedForShipper): any {
  const payload: UpdateFreightCommand = {
    id: freight.id,
    type: FreightType.FULL,
    transportInfoPayload: {
      vehicleTypes: freight.vehicleTypes,
      loadingTypes: freight.loadingTypes,
      transportGoodsType: freight.waste ? FreightTransportGoodsType.WASTE : FreightTransportGoodsType.NORMAL,
      exchangePlatform: null,
      // TODO review this
      // transportTemperatureRanges:
      //   freight.temperatureMin && freight.temperatureMax
      //     ? [
      //         {
      //           [freight.temperatureMin.toString()]: freight.temperatureMax.toString(),
      //         },
      //       ]
      //     : [],
      transportTemperatureRanges: [],
      transportTemperatureUnit: TemperatureUnit.CELSIUS,
      sendMarketEmail: true,
    } as unknown as FreightTransportInfoPayload,
    documentType: FreightDocumentType.ECDP,
    instructions: freight.instructions,
    shipperFreightM2mReference: freight.reference,
    deliveryNoteInPaper: freight.deliveryNoteInPaperRequired ? true : null,
  } as unknown as UpdateFreightCommand;

  return payload;
}

export function transformToUpdateFreightStopCommand(
  stop: IStopDetailedForShipper,
  index: number,
  freightId: string,
): any {
  const payload: UpdateFreightStopCommand = {
    stopId: stop.id,
    position: index,
    type: stop.type,
    addressPayload: {
      id: stop.address.addressbookId,
      street: stop.address.street,
      extraLine: stop.address.extraLine,
      city: stop.address.city,
      postalCode: stop.address.postalCode,
      // state: stop.address.state,
      stateCode: stop.address.stateCode,
      // country: stop.address.country,
      countryCode: stop.address.countryCode,
      pointPayload: stop.address.coords,
    },
    companyPayload: {
      name: stop.address.company,
      taxId: stop.address.taxId,
      phone: stop.address.phone,
      addressPayload: null,
      m2mId: null,
    },
    since: stop.since ? new Date(stop.since).toISOString() : null,
    until: stop.until ? new Date(stop.until).toISOString() : null,
    freightId: freightId,
    tare: null,
    grossWeight: null,
    netWeight: stop.netWeight || null,
    packages: stop.packages || null,
    goodsDescription: stop.goodsDescription || null,
    reference: stop.reference || null,
    unloadedNetWeight: stop.unloadedNetWeight || null,
    unloadedPackages: stop.unloadedPackages || null,
    instructions: stop.instructions,
  } as unknown as UpdateFreightStopCommand;

  return payload;
}
